import React, { useEffect, useState } from 'react';
import TcnThemeProvider from './ThemeProvider';
import AppShell from './layout/AppShell';
import TcnDesktopShell from './layout/TcnDesktopShell';

/**
 * TcnApp — viewport-aware wrapper that picks the right shell.
 *  - ≥1024px → <TcnDesktopShell>
 *  - <1024px → <AppShell> (mobile)
 *
 * Always wraps children in <TcnThemeProvider forceTcn={...}> so the dark theme
 * applies even before tenant resolution.
 *
 * Props:
 *  - tab, onTabChange — forwarded to whichever shell is active
 *  - mobileHeader — passed as `header` to <AppShell>
 *  - desktopBreadcrumb, desktopUserName, desktopRightSlot, onBell — desktop options
 *  - shellMode: 'auto' | 'mobile' | 'desktop' (default 'auto')
 *  - forceTcn: boolean — apply TCN theme regardless of tenantSlug (default true,
 *    since this component is always used inside /tcn/* routes)
 *  - children: page content
 */
const useIsDesktop = (breakpoint = 1024) => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(`(min-width: ${breakpoint}px)`).matches
      : true
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener
      ? mql.addEventListener('change', handler)
      : mql.addListener(handler);
    return () => {
      mql.removeEventListener
        ? mql.removeEventListener('change', handler)
        : mql.removeListener(handler);
    };
  }, [breakpoint]);

  return isDesktop;
};

const TcnApp = ({
  tab,
  onTabChange,
  mobileHeader,
  desktopBreadcrumb,
  desktopUserName,
  desktopRightSlot,
  onBell,
  shellMode = 'auto',
  forceTcn = true,
  children,
}) => {
  const autoDesktop = useIsDesktop();
  const isDesktop = shellMode === 'desktop' || (shellMode === 'auto' && autoDesktop);

  return (
    <TcnThemeProvider forceTcn={forceTcn}>
      {isDesktop ? (
        <TcnDesktopShell
          tab={tab}
          onTabChange={onTabChange}
          breadcrumb={desktopBreadcrumb}
          userName={desktopUserName}
          rightSlot={desktopRightSlot}
          onBell={onBell}
        >
          {children}
        </TcnDesktopShell>
      ) : (
        <AppShell
          tab={tab}
          onTabChange={onTabChange}
          header={mobileHeader}
        >
          {children}
        </AppShell>
      )}
    </TcnThemeProvider>
  );
};

export default TcnApp;
